import { useState } from 'react';
import { ArrowDown, ArrowUp, LayoutGrid, Pencil, Plus, Power } from 'lucide-react';
import {
  APPLICATION_STATUS,
  type ApplicationWithAccess,
} from '@electrodavisan/shared';
import {
  ApplicationStatusBadge,
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  PageHeader,
  SkeletonList,
  Table,
  useToast,
  type Column,
} from '../../components/ui';
import { formatDateTime } from '../../lib/format';
import {
  useAdminApplications,
  useReorderApplications,
  useUpdateApplication,
} from '../../services/applications.service';
import { ApplicationFormModal } from './ApplicationFormModal';

export function AdminApplicationsPage(): JSX.Element {
  const toast = useToast();
  const { data: applications, isLoading, isError, refetch } = useAdminApplications();
  const update = useUpdateApplication();
  const reorder = useReorderApplications();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ApplicationWithAccess | null>(null);
  const [toToggle, setToToggle] = useState<ApplicationWithAccess | null>(null);

  const openCreate = (): void => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (application: ApplicationWithAccess): void => {
    setEditing(application);
    setFormOpen(true);
  };

  /** Mueve una aplicacion una posicion arriba o abajo. */
  const move = (index: number, direction: -1 | 1): void => {
    if (!applications) return;
    const target = index + direction;
    if (target < 0 || target >= applications.length) return;

    const next = [...applications];
    [next[index], next[target]] = [next[target], next[index]];

    reorder.mutate(
      next.map((application, position) => ({ id: application.id, sortOrder: position })),
      { onError: () => toast.error('No se pudo reordenar') },
    );
  };

  const confirmToggle = (): void => {
    if (!toToggle) return;
    const disabling = toToggle.status !== APPLICATION_STATUS.INACTIVE;

    update.mutate(
      {
        id: toToggle.id,
        values: {
          status: disabling ? APPLICATION_STATUS.INACTIVE : APPLICATION_STATUS.ACTIVE,
        },
      },
      {
        onSuccess: () => {
          toast.success(disabling ? 'Aplicacion desactivada' : 'Aplicacion activada');
          setToToggle(null);
        },
        onError: () => toast.error('No se pudo cambiar el estado'),
      },
    );
  };

  const columns: Array<Column<ApplicationWithAccess>> = [
    {
      key: 'order',
      header: 'Orden',
      className: 'w-24',
      render: (application) => {
        const index = applications?.indexOf(application) ?? 0;
        return (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => move(index, -1)}
              disabled={index === 0 || reorder.isPending}
              className="rounded p-1 text-text-muted transition-colors hover:bg-surface-hover hover:text-text-primary disabled:opacity-30"
              aria-label="Subir"
            >
              <ArrowUp className="h-3.5 w-3.5" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => move(index, 1)}
              disabled={index === (applications?.length ?? 1) - 1 || reorder.isPending}
              className="rounded p-1 text-text-muted transition-colors hover:bg-surface-hover hover:text-text-primary disabled:opacity-30"
              aria-label="Bajar"
            >
              <ArrowDown className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
        );
      },
    },
    {
      key: 'name',
      header: 'Aplicacion',
      render: (application) => (
        <div className="min-w-0 max-w-xs">
          <p className="truncate font-medium text-text-primary">{application.name}</p>
          <p className="truncate font-mono text-[12px] text-text-muted">{application.slug}</p>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Estado',
      render: (application) => <ApplicationStatusBadge status={application.status} />,
    },
    {
      key: 'auth',
      header: 'Auth',
      hideBelow: 'md',
      render: (application) => <Badge>{application.authMode}</Badge>,
    },
    {
      key: 'access',
      header: 'Accesos',
      hideBelow: 'lg',
      render: (application) => (
        <span className="text-[13px]">
          {application.accessUsers.length === 1
            ? '1 persona'
            : `${application.accessUsers.length} personas`}
        </span>
      ),
    },
    {
      key: 'updated',
      header: 'Actualizada',
      hideBelow: 'xl',
      render: (application) => (
        <span className="whitespace-nowrap text-[13px]">
          {formatDateTime(application.updatedAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (application) => (
        <div className="flex justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => openEdit(application)}
            leftIcon={<Pencil className="h-3.5 w-3.5" aria-hidden />}
          >
            Editar
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setToToggle(application)}
            leftIcon={<Power className="h-3.5 w-3.5" aria-hidden />}
          >
            {application.status === APPLICATION_STATUS.INACTIVE ? 'Activar' : 'Desactivar'}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Administracion de aplicaciones"
        description="Registra aplicaciones del ecosistema y controla su estado."
        actions={
          <Button onClick={openCreate} leftIcon={<Plus className="h-4 w-4" aria-hidden />}>
            Registrar aplicacion
          </Button>
        }
      />

      {isError ? (
        <ErrorState onRetry={() => void refetch()} />
      ) : isLoading ? (
        <SkeletonList rows={4} />
      ) : (
        <Table
          columns={columns}
          rows={applications ?? []}
          rowKey={(application) => application.id}
          empty={
            <EmptyState
              icon={<LayoutGrid className="h-6 w-6" aria-hidden />}
              title="Todavia no hay aplicaciones vinculadas al Ecosystem"
              description="Registra la primera aplicacion para comenzar a construir el ecosistema. Recuerda crear tambien su client OIDC en Keycloak."
              action={
                <Button onClick={openCreate} leftIcon={<Plus className="h-4 w-4" aria-hidden />}>
                  Registrar la primera aplicacion
                </Button>
              }
            />
          }
        />
      )}

      <ApplicationFormModal
        open={formOpen}
        application={editing}
        onClose={() => setFormOpen(false)}
      />

      <ConfirmDialog
        open={Boolean(toToggle)}
        title={
          toToggle?.status === APPLICATION_STATUS.INACTIVE
            ? 'Activar aplicacion'
            : 'Desactivar aplicacion'
        }
        message={
          toToggle?.status === APPLICATION_STATUS.INACTIVE
            ? `"${toToggle?.name}" volvera a aparecer para los usuarios autorizados.`
            : `"${toToggle?.name}" dejara de aparecer en el launcher. Los tickets existentes se conservan.`
        }
        confirmLabel={
          toToggle?.status === APPLICATION_STATUS.INACTIVE ? 'Activar' : 'Desactivar'
        }
        tone={toToggle?.status === APPLICATION_STATUS.INACTIVE ? 'primary' : 'danger'}
        loading={update.isPending}
        onConfirm={confirmToggle}
        onCancel={() => setToToggle(null)}
      />
    </>
  );
}
